import { Component } from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { Observable, tap } from 'rxjs';
import {
  ReorderDetailsItem,
  ApiReleaseShipmentRequest,
  ApiPaymentRequest,
  PlanType,
  PaymentMethodType,
} from 'src/app/core/models';
import { ResponsiveUtilsService, ApiService } from 'src/app/core/services';
import { OrderWizardDataState } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.state';
import { ProductItemQuantityUserData } from 'src/app/core/store/order-wizard/order-wizard-data/order-wizard-data.model';
import { WizardStep } from 'src/app/core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.model';
import { ApiStoreData, CcsSentryError } from 'src/app/core/store/utils';
import { OrderWizardNavigationState } from 'src/app/core/store/order-wizard/order-wizard-navigation/order-wizard-navigation.state';
import { PatientState } from 'src/app/core/store/patient/patient.state';
import { Router } from '@angular/router';
import * as moment from 'moment';

@Component({
  selector: 'app-billing-step',
  templateUrl: './billing-step.component.html',
  styleUrls: ['./billing-step.component.scss'],
})
export class BillingStepComponent {
  @Select(OrderWizardDataState.isBillingStepLoading) isBillingStepLoading$!: Observable<boolean>;
  @Select(OrderWizardDataState.billingStepError) billingStepError$!: Observable<CcsSentryError | null>;
  @Select(OrderWizardDataState.reorderItemsSummary) reorderItemsSummary$!: Observable<
    (ReorderDetailsItem & ProductItemQuantityUserData)[]
  >;
  @Select(OrderWizardDataState.updateCopayDeductibleOverridesState)
  updateCopayDeductibleOverridesState$!: Observable<ApiStoreData<null>>;

  @Select(OrderWizardNavigationState.activeStep) activeStep$!: Observable<WizardStep>;
  @Select(OrderWizardNavigationState.activeIndex) activeIndex$!: Observable<number>;

  isConfirmationTouched: boolean = false;
  editMode: boolean = false;
  isPaymentMethodTouched: boolean = false;
  confirmDeductibleOverrides: boolean = false;
  isLgAndAbove$: Observable<boolean> = this.responsiveUtils.isLgAndAbove();
  isProcessingPayment = false;
  displayPaymentError: boolean = false;
  displayPaymentSuccess: boolean = false;
  displayHealthSavingsAccountModal: boolean = false;
  displayShipmentReleaseError: boolean = false;
  shipmentReleaseErrorMessage: string | null = null;
  get isMakePaymentPage(): boolean {
    return this.router.url?.includes('make-payment') || false;
  }

  constructor(
    private store: Store,
    public responsiveUtils: ResponsiveUtilsService,
    private router: Router,
    private apiService: ApiService
  ) {}

  onSubmit(): boolean {
    const hasUnsubmittedDeductibleOverrides = this.store.selectSnapshot(
      OrderWizardDataState.hasUnsubmittedDeductibleOverrides
    );

    if (hasUnsubmittedDeductibleOverrides) {
      this.confirmDeductibleOverrides = true;
      return false;
    }

    this.isConfirmationTouched = true;
    this.confirmDeductibleOverrides = false;
    let isSuccessful = this.store.selectSnapshot(OrderWizardDataState.isOrderConfirmed);

    const paymentOptional = this.store.selectSnapshot(OrderWizardDataState.paymentOptional);
    const selectedPaymentMethod = this.store.selectSnapshot(OrderWizardDataState.selectedPaymentMethod);

    if (
      !paymentOptional &&
      (!selectedPaymentMethod ||
        (selectedPaymentMethod.type === PaymentMethodType.CreditCard && selectedPaymentMethod.info.isExpired))
    ) {
      this.isPaymentMethodTouched = true;
      isSuccessful = false;
    }

    if (this.router.url.includes('make-payment') && isSuccessful) {
      this.processPaymentAndReleaseShipment();
      return false;
    }

    return isSuccessful;
  }

  processPaymentAndReleaseShipment() {
    const patientId = this.store.selectSnapshot(PatientState.selectedPatientId);
    const selectedPaymentMethod = this.store.selectSnapshot(OrderWizardDataState.selectedPaymentMethod);
    const copayData = this.store.selectSnapshot(OrderWizardDataState.copay);
    const reorderId = this.store.selectSnapshot(OrderWizardDataState.reorder)?.id;
    const targetShipDate = moment(this.store.selectSnapshot(OrderWizardDataState.computedTargetShipDate))
      .utc()
      .format('YYYY-MM-DD[T]HH:mm:ss[Z]');

    const paymentRequestBody: ApiPaymentRequest = {
      planType: PlanType.OneTime,
      total: copayData?.estimatedPatientResponsibility || 0,
      currency: 'USD',
      startedOn: moment.utc().format(),
      paymentMethodId: selectedPaymentMethod?.id || '',
    };

    // this is for the regular release (aka not HSA)
    const releaseShipmentRequestBody: ApiReleaseShipmentRequest = {
      patientId: patientId || 0,
      referenceNumber: reorderId || 0,
      targetedShipDate: targetShipDate,
      paymentProcessed: 'N',
      patientEstimatedCopay: copayData?.estimatedInsuranceCopayment || 0,
      patientBalance: copayData?.outstandingBalance || 0,
      patientResponsibility: copayData?.estimatedPatientResponsibility || 0,
    };

    // this is for the HSA release
    // the difference is that the paymentProcessed is 'N' and the paymentType is 'HSA'
    // this is because the HSA is not processed until the EOB is received and we do not want to take a payment
    const releaseShipmentHSARequestBody: ApiReleaseShipmentRequest = {
      patientId: patientId || 0,
      referenceNumber: reorderId || 0,
      targetedShipDate: targetShipDate,
      paymentProcessed: 'N',
      paymentType: 'HSA',
    };

    // this is to show the loading button
    this.isProcessingPayment = true;

    const paymentType = selectedPaymentMethod?.type;
    if (paymentType === PaymentMethodType.CreditCard && selectedPaymentMethod?.info.isHealthAccount) {
      this.apiService
        .releaseShipment(releaseShipmentHSARequestBody)
        .pipe(
          tap({
            next: () => {
              this.displayHealthSavingsAccountModal = true;
            },
            error: (error: any) => {
              // The API returns errors in error.error.errors array
              this.shipmentReleaseErrorMessage = error?.error?.errors?.[0] || 'Failed to release shipment';
              this.displayShipmentReleaseError = true;
            },
            finalize: () => {
              this.isProcessingPayment = false;
            },
          })
        )
        .subscribe();
      return;
    }

    // we need to make the first call to the payments api
    // we need to make the second call to the releaseShipment api after the first call is successful
    this.apiService
      .makePayment(paymentRequestBody, patientId || 0)
      .pipe(
        tap({
          next: () => {
            // Since we are here, the payment is successful and processed
            releaseShipmentRequestBody.paymentProcessed = 'Y';
            releaseShipmentRequestBody.patientResponsibility = copayData?.estimatedPatientResponsibility || 0;
            releaseShipmentRequestBody.patientEstimatedCopay = copayData?.estimatedInsuranceCopayment || 0;
            releaseShipmentRequestBody.patientBalance = copayData?.outstandingBalance || 0;

            // Since the payment is successful, call the releaseShipment api
            this.releaseShipment(releaseShipmentRequestBody);
          },
          error: () => {
            this.isProcessingPayment = false;
            this.displayPaymentError = true;
          },
        })
      )
      .subscribe();
  }

  releaseShipment(releaseShipmentRequestBody: ApiReleaseShipmentRequest) {
    this.apiService
      .releaseShipment(releaseShipmentRequestBody)
      .pipe(
        tap({
          next: () => {},
          error: (error: any) => {
            // The API returns errors in error.error.errors array
            this.shipmentReleaseErrorMessage = error?.error?.errors?.[0] || 'Failed to release shipment';
            this.displayShipmentReleaseError = true;
          },
          finalize: () => {
            this.isProcessingPayment = false;
            this.displayPaymentSuccess = true;
          },
        })
      )
      .subscribe();
  }

  onEditModeChange(editMode: boolean) {
    this.editMode = editMode;
  }

  paymentFailedNotificationDismissed() {
    this.displayPaymentError = false;
  }

  paymentSuccessNotificationDismissed() {
    this.router.navigate(['/']);
  }

  healthSavingsAccountModalDismissed() {
    this.displayHealthSavingsAccountModal = false;
    this.router.navigate(['/']);
  }

  shipmentReleaseErrorDismissed() {
    this.displayShipmentReleaseError = false;
    this.shipmentReleaseErrorMessage = null;
  }
}
